
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface User {
  id: string;
  name: string;
  email: string;
  promptsCount: number;
  joinDate: string;
  status: string;
}

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, created_at, role');

      if (profilesError) throw profilesError;

      const usersWithData = await Promise.all(
        profilesData.map(async (profile) => {
          const { count, error: countError } = await supabase
            .from('prompts')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', profile.id);

          if (countError) console.error('Error counting prompts:', countError);

          return {
            id: profile.id,
            name: [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown',
            email: 'N/A',
            promptsCount: count || 0,
            joinDate: profile.created_at,
            status: profile.role === 'admin' ? 'admin' : 'user'
          };
        })
      );

      setUsers(usersWithData);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error fetching users",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const deleteUser = async (userId: string): Promise<boolean> => {
    if (!userId) {
      toast({
        title: "No user ID provided",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      setIsDeleting(true);
      
      console.log(`Starting deletion process for user: ${userId}`);
      
      // First check if we have a valid auth token
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        console.error('Error getting auth session for delete operation:', sessionError);
        toast({
          title: 'Authentication required',
          description: 'You must be logged in to delete a user',
          variant: "destructive",
        });
        return false;
      }
      
      // Pre-deletion validation
      const { data: userExists, error: userCheckError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .single();
        
      if (userCheckError || !userExists) {
        console.error('User validation error:', userCheckError);
        toast({
          title: 'User not found',
          description: 'The user you are trying to delete may not exist',
          variant: "destructive",
        });
        return false;
      }
      
      // Call our edge function to handle the deletion with admin privileges
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId }
      });
      
      if (error) {
        console.error('Error calling delete-user function:', error);
        toast({
          title: 'Deletion failed',
          description: `Unable to delete user: ${error.message}`,
          variant: "destructive",
        });
        return false;
      }
      
      if (!data || !data.success) {
        console.error('Delete user function reported failure:', data);
        toast({
          title: 'Deletion failed',
          description: data?.message || 'The server reported an error during user deletion',
          variant: "destructive",
        });
        return false;
      }
      
      // Success!
      console.log('User deletion completed successfully:', data);
      return true;
    } catch (error: any) {
      console.error('Error in user deletion process:', error);
      toast({
        title: 'Unexpected error',
        description: `A system error occurred: ${error.message}`,
        variant: "destructive", 
      });
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return {
    users,
    isLoadingUsers,
    fetchUsers,
    deleteUser,
    isDeleting
  };
};
